import { Controller } from "@hotwired/stimulus"

// Filters activity dropdown by target type; toggles activity / scheduled fields by kind.
export default class extends Controller {
  static targets = [
    "targetType",
    "targetId",
    "activitySelect",
    "targetIdWrapper",
    "kindSelect",
    "kindDescription",
    "kindDescriptionDatum",
    "activityFields",
    "scheduledFields",
    "inactivityFields",
    "inactivityTargetType",
    "sinceActivitySelect",
    "inactivityTargetIdWrapper",
    "inactivityTargetId"
  ]
  static values = {
    activities: Object,
    targetOptions: Object,
    typesWithoutSpecificItem: Array
  }

  connect() {
    this.toggleKind()
    this.filterOptions()
    this.filterInactivityOptions()
  }

  onFormChange(event) {
    if (this.hasKindSelectTarget && this.element.contains(event.target) && event.target === this.kindSelectTarget) {
      this.toggleKind()
    }
  }

  toggleKind() {
    const kind = this.hasKindSelectTarget ? this.kindSelectTarget.value : "activity"
    this.updateKindDescription(kind)
    if (this.hasActivityFieldsTarget) {
      const show = kind === "activity"
      this.activityFieldsTarget.classList.toggle("hidden", !show)
      this.setSectionDisabled(this.activityFieldsTarget, !show)
    }
    if (this.hasInactivityFieldsTarget) {
      const show = kind === "inactivity"
      this.inactivityFieldsTarget.classList.toggle("hidden", !show)
      this.setSectionDisabled(this.inactivityFieldsTarget, !show)
    }
    if (this.hasScheduledFieldsTarget) {
      const show = kind === "scheduled"
      this.scheduledFieldsTarget.classList.toggle("hidden", !show)
      this.setSectionDisabled(this.scheduledFieldsTarget, !show)
    }
  }

  updateKindDescription(kind) {
    if (!this.hasKindDescriptionTarget) return
    const datum = this.kindDescriptionDatumTargets.find((el) => el.dataset.kind === kind) ||
      this.kindDescriptionDatumTargets.find((el) => el.dataset.kind === "activity")
    if (!datum) return
    const { title, body, classes } = datum.dataset
    this.kindDescriptionTarget.className = `rounded-lg border-l-4 p-3 mb-4 text-sm ${classes}`
    this.kindDescriptionTarget.innerHTML =
      `<p class="font-semibold mb-1">${this.escapeHtml(title)}</p><p>${body}</p>`
  }

  setSectionDisabled(section, disabled) {
    section.querySelectorAll("input, select, textarea").forEach((el) => {
      el.disabled = disabled
    })
    section.querySelectorAll("[data-controller~='multiselect']").forEach((wrapper) => {
      if (wrapper.choicesInstance) {
        disabled ? wrapper.choicesInstance.disable() : wrapper.choicesInstance.enable()
      }
    })
  }

  filterOptions() {
    const targetType = this.hasTargetTypeTarget ? this.targetTypeTarget.value : ""
    this.filterActivityOptions(targetType)
    this.filterTargetIdOptions(targetType)
    this.toggleTargetIdWrapper(targetType)
  }

  filterInactivityOptions() {
    const targetType = this.hasInactivityTargetTypeTarget ? this.inactivityTargetTypeTarget.value : ""
    this.filterSinceActivityOptions(targetType)
    this.filterInactivityTargetIdOptions(targetType)
    this.toggleInactivityTargetIdWrapper(targetType)
  }

  /** Turns e.g. collection.load.qr_code into "Collection Load Qr Code" for select labels. */
  formatActivityLabel(name) {
    return name
      .replace(/[._]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  filterSinceActivityOptions(targetType) {
    if (!this.hasSinceActivitySelectTarget) return
    const activities = this.activitiesValue || {}
    const list = targetType && activities[targetType] ? activities[targetType] : this.allActivityNames()
    const selected = this.sinceActivitySelectTarget.value
    const choices = [
      { value: "", label: "Any activity (pure inactivity)" },
      ...list.map((name) => ({
        value: name,
        label: this.formatActivityLabel(name)
      }))
    ]
    this.sinceActivitySelectTarget.innerHTML = choices
      .map((c) => `<option value="${this.escapeHtml(c.value)}"${c.value === selected ? " selected" : ""}>${this.escapeHtml(c.label)}</option>`)
      .join("")
  }

  filterInactivityTargetIdOptions(targetType) {
    if (!this.hasInactivityTargetIdTarget) return
    const options = this.targetOptionsValue || {}
    const list = targetType && options[targetType] ? options[targetType] : []
    const selected = this.inactivityTargetIdTarget.value
    const choices = [{ value: "", label: "Any" }, ...list.map(([label, id]) => ({ value: String(id), label: label || String(id) }))]
    const wrapper = this.inactivityTargetIdTarget.closest("[data-controller~='multiselect']")
    this.updateResourceTypesForWrapper(wrapper, list, targetType)
    if (wrapper && wrapper.choicesInstance) {
      wrapper.choicesInstance.setChoices(choices, "value", "label", true)
      if (selected !== undefined && selected !== null) wrapper.choicesInstance.setChoiceByValue(String(selected))
    } else {
      this.inactivityTargetIdTarget.innerHTML =
        '<option value="">Any</option>' +
        list
          .map(
            ([label, id]) =>
              `<option value="${id}"${id === selected || id.toString() === selected ? " selected" : ""}>${this.escapeHtml(label || "")}</option>`
          )
          .join("")
    }
  }

  toggleInactivityTargetIdWrapper(targetType) {
    if (!this.hasInactivityTargetIdWrapperTarget) return
    const typesWithout = this.typesWithoutSpecificItemValue || []
    const hide = targetType && typesWithout.includes(targetType)
    this.inactivityTargetIdWrapperTarget.classList.toggle("hidden", hide)
  }

  toggleTargetIdWrapper(targetType) {
    if (!this.hasTargetIdWrapperTarget) return
    const typesWithout = this.typesWithoutSpecificItemValue || []
    const hide = targetType && typesWithout.includes(targetType)
    this.targetIdWrapperTarget.classList.toggle("hidden", hide)
  }

  filterActivityOptions(targetType) {
    if (!this.hasActivitySelectTarget) return
    const activities = this.activitiesValue || {}
    const list = targetType && activities[targetType] ? activities[targetType] : this.allActivityNames()
    const selected = this.activitySelectTarget.value
    const choices = [
      { value: "", label: "" },
      ...list.map((name) => ({
        value: name,
        label: this.formatActivityLabel(name)
      }))
    ]
    this.activitySelectTarget.innerHTML = choices
      .map((c) => `<option value="${this.escapeHtml(c.value)}"${c.value === selected ? " selected" : ""}>${this.escapeHtml(c.label)}</option>`)
      .join("")
  }

  allActivityNames() {
    const activities = this.activitiesValue || {}
    const set = new Set()
    Object.values(activities).forEach((arr) => arr.forEach((a) => set.add(a)))
    return Array.from(set).sort()
  }

  filterTargetIdOptions(targetType) {
    if (!this.hasTargetIdTarget) return
    const options = this.targetOptionsValue || {}
    const list = targetType && options[targetType] ? options[targetType] : []
    const selected = this.targetIdTarget.value
    const choices = [{ value: "", label: "Any" }, ...list.map(([label, id]) => ({ value: String(id), label: label || String(id) }))]
    const wrapper = this.targetIdTarget.closest("[data-controller~='multiselect']")
    this.updateResourceTypesForWrapper(wrapper, list, targetType)
    if (wrapper && wrapper.choicesInstance) {
      wrapper.choicesInstance.setChoices(choices, "value", "label", true)
      if (selected !== undefined && selected !== null) wrapper.choicesInstance.setChoiceByValue(String(selected))
    } else {
      this.targetIdTarget.innerHTML =
        '<option value="">Any</option>' +
        list
          .map(
            ([label, id]) =>
              `<option value="${id}"${id === selected || id.toString() === selected ? " selected" : ""}>${this.escapeHtml(label || "")}</option>`
          )
          .join("")
    }
  }

  updateResourceTypesForWrapper(wrapper, list, targetType) {
    if (!wrapper || !targetType) return
    const resourceTypes = {}
    list.forEach(([, id]) => { resourceTypes[String(id)] = targetType })
    wrapper.dataset.multiselectResourceTypesValue = JSON.stringify(resourceTypes)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
